import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import {
  DEMO_ACTIVATION_REQUEST_NOTICE,
  ClientApiError,
  api,
  isDemoActivationRequired,
} from '../api'
import { tradingServerLabel } from '../branding'
import { fixtureAccount, fixtureSummary, fixtureTrades, useFixturesFromQuery } from '../fixtures'
import { useAppPath } from '../nav'
import { useWorkspaceAccount } from '../WorkspaceAccountProvider'
import type {
  AccountActivityItem,
  AccountSummary,
  Position,
  TradeHistoryItem,
  TradingAccount,
} from '../types'
import {
  activityTypeLabel,
  formatDateOnly,
  formatWhen,
  isSelectableAccount,
  maskLogin,
  moneySigned,
  safeError,
} from '../safe-ui'
import { Button } from '../../components/ui/Button'

type PlFilter = 'ALL' | 'PROFIT' | 'LOSS'
type ViewTab = 'trades' | 'activity'

function plClass(n: number | null | undefined): string {
  if (n == null || Number.isNaN(Number(n))) return ''
  const v = Number(n)
  if (v > 0) return 'cp-th__pl--pos'
  if (v < 0) return 'cp-th__pl--neg'
  return ''
}

function accountMode(mode?: string | null): string {
  return String(mode || '').toUpperCase()
}

function isLiveMode(mode?: string | null): boolean {
  const m = accountMode(mode)
  return m === 'LIVE' || m === 'REAL'
}

function preferHistoryAccountId(active: TradingAccount[], prev: string): string {
  if (prev && active.some((a) => String(a.id) === prev)) return prev
  // Predictions are placed on Demo; Live is often empty after account create.
  const demoPreferred = active.find((a) => accountMode(a.mode) === 'DEMO')
  const livePreferred = active.find((a) => isLiveMode(a.mode))
  const preferred = demoPreferred || livePreferred || active[0]
  return preferred ? String(preferred.id) : ''
}

function computeSummary(trades: TradeHistoryItem[]) {
  let wins = 0
  let losses = 0
  let grossProfit = 0
  let grossLoss = 0
  for (const t of trades) {
    const net = Number(t.net_profit ?? t.profit ?? 0)
    if (Number.isNaN(net)) continue
    if (net > 0) {
      wins += 1
      grossProfit += net
    } else if (net < 0) {
      losses += 1
      grossLoss += Math.abs(net)
    }
  }
  return {
    closed: trades.length,
    wins,
    losses,
    grossProfit,
    grossLoss,
    net: grossProfit - grossLoss,
  }
}

export function TradingHistoryPage() {
  const appPath = useAppPath()
  const fixtures = useFixturesFromQuery()
  const ws = useWorkspaceAccount()
  const [loading, setLoading] = useState(true)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [creatingAccount, setCreatingAccount] = useState(false)
  const [accounts, setAccounts] = useState<TradingAccount[]>([])
  const [summaries, setSummaries] = useState<Record<number, AccountSummary | null>>({})
  const [accountId, setAccountId] = useState('')
  const [tab, setTab] = useState<ViewTab>('trades')

  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [symbol, setSymbol] = useState('')
  const [side, setSide] = useState('')
  const [plFilter, setPlFilter] = useState<PlFilter>('ALL')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(50)

  const [trades, setTrades] = useState<TradeHistoryItem[]>([])
  const [openPositions, setOpenPositions] = useState<Position[]>([])
  const [activities, setActivities] = useState<AccountActivityItem[]>([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [historyUnavailable, setHistoryUnavailable] = useState(false)
  const [detail, setDetail] = useState<TradeHistoryItem | null>(null)

  const selectable = useMemo(
    () => accounts.filter((a) => isSelectableAccount(a.status)),
    [accounts],
  )
  const hasAccount = selectable.length > 0
  const selected = selectable.find((a) => String(a.id) === accountId) || null
  const selectedSummary = selected ? summaries[selected.id] : null
  const selectedIsLive = isLiveMode(selected?.mode)
  const hasDemoAccount = selectable.some((a) => accountMode(a.mode) === 'DEMO')
  const currency = selectedSummary?.currency || selected?.currency || 'USD'
  const filteredTrades = useMemo(() => {
    if (plFilter === 'ALL') return trades
    return trades.filter((t) => {
      const net = Number(t.net_profit ?? t.profit ?? 0)
      if (Number.isNaN(net)) return false
      return plFilter === 'PROFIT' ? net > 0 : net < 0
    })
  }, [trades, plFilter])

  const summary = useMemo(() => computeSummary(filteredTrades), [filteredTrades])

  const loadAccounts = useCallback(async () => {
    if (fixtures) {
      setAccounts([fixtureAccount])
      setAccountId(String(fixtureAccount.id))
      setSummaries({ [fixtureAccount.id]: fixtureSummary })
      return
    }
    const listed = await api.listAccounts()
    const active = (listed.accounts || []).filter((a) => isSelectableAccount(a.status))
    setAccounts(listed.accounts || [])
    setAccountId((prev) => preferHistoryAccountId(active, prev))
    // Summaries load in the background so the page is not stuck on Loading…
  }, [fixtures])

  useEffect(() => {
    if (fixtures || !selected?.id) return
    let cancelled = false
    void api
      .summary(selected.id)
      .then((s) => {
        if (!cancelled) {
          setSummaries((prev) => ({ ...prev, [selected.id]: s.account }))
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSummaries((prev) => ({ ...prev, [selected.id]: null }))
        }
      })
    return () => {
      cancelled = true
    }
  }, [selected?.id, fixtures])

  const loadHistory = useCallback(async () => {
    if (!selected) return
    setHistoryLoading(true)
    setError('')
    setHistoryUnavailable(false)
    try {
      if (fixtures) {
        setTrades(fixtureTrades)
        setOpenPositions([])
        setTotal(fixtureTrades.length)
        setTotalPages(1)
        setActivities([])
        return
      }
      if (tab === 'trades') {
        const [res, open] = await Promise.all([
          api.tradeHistory(selected.id, {
            from: dateFrom || undefined,
            to: dateTo || undefined,
            symbol: symbol.trim() || undefined,
            side: side || undefined,
            page,
            pageSize,
          }),
          api.openPositions(selected.id).catch(() => ({
            positions: [] as Position[],
            count: 0,
          })),
        ])
        setTrades(res.trades || res.deals || [])
        setOpenPositions(open.positions || [])
        setTotal(res.pagination?.total ?? (res.trades || []).length)
        setTotalPages(res.pagination?.total_pages ?? 1)
      } else {
        setOpenPositions([])
        const res = await api.accountActivity(selected.id, {
          from: dateFrom || undefined,
          to: dateTo || undefined,
          page,
          pageSize,
        })
        setActivities(res.activities || [])
        setTotal(res.pagination?.total ?? (res.activities || []).length)
        setTotalPages(res.pagination?.total_pages ?? 1)
      }
    } catch (err) {
      setHistoryUnavailable(true)
      setTrades([])
      setOpenPositions([])
      setActivities([])
      // Owned account already selected — never surface Bridge "not found" as account missing.
      const softSync =
        err instanceof ClientApiError &&
        (err.code === 'MT5_SYNC_UNAVAILABLE' ||
          err.code === 'MT5_UNAVAILABLE' ||
          /account not found/i.test(err.message || '') ||
          /temporarily unavailable/i.test(err.message || ''))
      setError(
        softSync
          ? ''
          : safeError(err, 'Unable to load trading history. Please try again.'),
      )
    } finally {
      setHistoryLoading(false)
    }
  }, [selected, fixtures, tab, dateFrom, dateTo, symbol, side, page, pageSize])

  useEffect(() => {
    let cancelled = false
    async function run() {
      setLoading(true)
      setError('')
      try {
        await loadAccounts()
      } catch (err) {
        if (!cancelled) setError(safeError(err, 'Unable to load trading history. Please try again.'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [loadAccounts])

  useEffect(() => {
    if (!hasAccount || !selected) return
    void loadHistory()
  }, [hasAccount, selected, loadHistory])

  useEffect(() => {
    if (!selected?.id || ws.selectedAccountId === selected.id) return
    ws.setAccountId(selected.id)
  }, [selected?.id, ws.selectedAccountId, ws.setAccountId])

  async function requestDemoAccount() {
    setCreatingAccount(true)
    setError('')
    setNotice('')
    try {
      await api.createAccount(`web-th-${Date.now()}`)
      setNotice('Demo trading account created.')
      await loadAccounts()
    } catch (err) {
      if (isDemoActivationRequired(err)) {
        setNotice(DEMO_ACTIVATION_REQUEST_NOTICE)
        return
      }
      setError(
        'Demo account creation is temporarily unavailable. Please contact support or try again later.',
      )
    } finally {
      setCreatingAccount(false)
    }
  }

  function applyFilters(e: FormEvent) {
    e.preventDefault()
    if (page !== 1) setPage(1)
    else void loadHistory()
  }

  return (
    <section className="cp-page cp-th">
      <header className="cp-th__head">
        <h1>Trading History</h1>
        <p>Closed trades and account activity.</p>
      </header>
      {loading ? (
        <p className="cp-muted ap-inline-status" aria-live="polite">
          Loading…
        </p>
      ) : null}
      {error ? (
        <div className="cp-th__alert cp-th__alert--error" role="alert">
          <p>{error}</p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void (hasAccount ? loadHistory() : loadAccounts())}
          >
            Retry
          </Button>
        </div>
      ) : null}
      {notice ? <p className="cp-ok">{notice}</p> : null}

      {!loading && !hasAccount ? (
        <div className="cp-th__blocked">
          <span className="cp-badge cp-badge--mt5">Powered by MT5</span>
          <h2>Create a Trading Account First</h2>
          <p>
            Your MT5 trading history will appear here after your account is activated and trades are
            completed.
          </p>
          <div className="cp-th__blocked-actions">
            <Button to={appPath('/app/accounts')} variant="primary">
              Trading Accounts
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={creatingAccount}
              onClick={() => void requestDemoAccount()}
            >
              {creatingAccount ? 'Submitting…' : 'Request Demo Account'}
            </Button>
          </div>
        </div>
      ) : null}

      {loading || (hasAccount && selected) ? (
        <>
          {!loading && selected && selectable.length >= 1 ? (
            <div className="cp-th__mode-bar">
              <div className="cp-account-switcher" role="group" aria-label="Account">
                <button
                  type="button"
                  className={selectedIsLive ? 'is-active' : ''}
                  disabled={!selectable.some((a) => isLiveMode(a.mode))}
                  onClick={() => {
                    const a = selectable.find((x) => isLiveMode(x.mode))
                    if (a) {
                      setAccountId(String(a.id))
                      setPage(1)
                    }
                  }}
                >
                  LIVE
                </button>
                <button
                  type="button"
                  className={accountMode(selected.mode) === 'DEMO' ? 'is-active' : ''}
                  disabled={!hasDemoAccount}
                  onClick={() => {
                    const a = selectable.find((x) => accountMode(x.mode) === 'DEMO')
                    if (a) {
                      setAccountId(String(a.id))
                      setPage(1)
                    }
                  }}
                >
                  DEMO
                </button>
              </div>
              {selectable.length > 1 ? (
                <label className="cp-th__account-select cp-workspace-select">
                  <span className="visually-hidden">Trading account</span>
                  <select
                    value={String(selected.id)}
                    onChange={(e) => {
                      setAccountId(e.target.value)
                      setPage(1)
                    }}
                  >
                    {selectable.map((a) => (
                      <option key={a.id} value={a.id}>
                        {maskLogin(a.mt5Login)} · {tradingServerLabel(a.mode, a.server)}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <p className="cp-muted cp-th__account-hint">
                  {maskLogin(selected.mt5Login)} · {tradingServerLabel(selected.mode, selected.server)}
                </p>
              )}
            </div>
          ) : loading ? (
            <div className="cp-th__mode-bar is-refreshing" aria-hidden="true" />
          ) : null}

          <div className="cp-th__tabs" role="tablist" aria-label="History views">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'trades'}
              className={tab === 'trades' ? 'is-active' : ''}
              onClick={() => {
                setTab('trades')
                setPage(1)
              }}
            >
              Closed Trades
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'activity'}
              className={tab === 'activity' ? 'is-active' : ''}
              onClick={() => {
                setTab('activity')
                setPage(1)
              }}
            >
              Account Activity
            </button>
          </div>

          <form className="cp-th__filters" onSubmit={applyFilters}>
            <label>
              <span>From</span>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </label>
            <label>
              <span>To</span>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </label>
            {tab === 'trades' ? (
              <>
                <label>
                  <span>Symbol</span>
                  <input
                    type="text"
                    value={symbol}
                    onChange={(e) => setSymbol(e.target.value)}
                    placeholder="e.g. EURUSD"
                    autoComplete="off"
                  />
                </label>
                <label>
                  <span>Side</span>
                  <select value={side} onChange={(e) => setSide(e.target.value)}>
                    <option value="">All</option>
                    <option value="BUY">BUY</option>
                    <option value="SELL">SELL</option>
                  </select>
                </label>
                <label>
                  <span>Result</span>
                  <select
                    value={plFilter}
                    onChange={(e) => setPlFilter(e.target.value as PlFilter)}
                  >
                    <option value="ALL">All</option>
                    <option value="PROFIT">Profit</option>
                    <option value="LOSS">Loss</option>
                  </select>
                </label>
              </>
            ) : null}
            <div className="cp-th__filter-actions">
              <Button type="submit" variant="secondary" size="sm" disabled={historyLoading}>
                Apply
              </Button>
            </div>
          </form>

          {tab === 'trades' ? (
            !loading && historyUnavailable ? (
              <p className="cp-muted">Trading history is temporarily unavailable.</p>
            ) : (
              <>
                <section
                  className={`cp-th__open${loading || historyLoading ? ' is-refreshing' : ''}`}
                  aria-label="Open positions"
                >
                  <div className="cp-th__open-head">
                    <h2>Open positions</h2>
                    <span className="cp-muted">
                      {loading || historyLoading ? 'Updating…' : `${openPositions.length} open`}
                    </span>
                  </div>
                  {loading || historyLoading ? (
                    <p className="cp-muted ap-inline-status" aria-live="polite">
                      Syncing open positions…
                    </p>
                  ) : openPositions.length === 0 ? (
                    <p className="cp-muted">No open positions on this account.</p>
                  ) : (
                    <div className="cp-th__table-wrap">
                      <table className="cp-th__table">
                        <thead>
                          <tr>
                            <th>Ticket</th>
                            <th>Symbol</th>
                            <th>Side</th>
                            <th>Volume</th>
                            <th>Open Price</th>
                            <th>Current</th>
                            <th>Floating P/L</th>
                          </tr>
                        </thead>
                        <tbody>
                          {openPositions.map((p) => (
                            <tr key={p.ticket}>
                              <td className="cp-th__num">{p.ticket}</td>
                              <td>{p.symbol}</td>
                              <td>{p.side}</td>
                              <td className="cp-th__num">{p.volume}</td>
                              <td className="cp-th__num">{p.open_price}</td>
                              <td className="cp-th__num">{p.current_price}</td>
                              <td className={`cp-th__num ${plClass(p.floating_profit)}`}>
                                {moneySigned(p.floating_profit, currency)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>

                <section
                  className={`cp-th__summary${loading || historyLoading ? ' is-refreshing' : ''}`}
                  aria-label="Trade summary"
                >
                  <div>
                    <span>Closed Trades</span>
                    <strong className="cp-th__num">{summary.closed}</strong>
                  </div>
                  <div>
                    <span>Winning</span>
                    <strong className="cp-th__num">{summary.wins}</strong>
                  </div>
                  <div>
                    <span>Losing</span>
                    <strong className="cp-th__num">{summary.losses}</strong>
                  </div>
                  <div>
                    <span>Gross Profit</span>
                    <strong className={`cp-th__num ${plClass(summary.grossProfit)}`}>
                      {moneySigned(summary.grossProfit, currency)}
                    </strong>
                  </div>
                  <div>
                    <span>Gross Loss</span>
                    <strong className={`cp-th__num ${plClass(-summary.grossLoss)}`}>
                      {moneySigned(-summary.grossLoss, currency)}
                    </strong>
                  </div>
                  <div>
                    <span>Net P/L</span>
                    <strong className={`cp-th__num ${plClass(summary.net)}`}>
                      {moneySigned(summary.net, currency)}
                    </strong>
                  </div>
                </section>

                {historyLoading && !loading ? (
                  <p className="cp-muted ap-inline-status" aria-live="polite">
                    Loading…
                  </p>
                ) : null}

                <div
                  className={`cp-th__table-wrap${loading || historyLoading ? ' is-refreshing' : ''}`}
                >
                  <table className="cp-th__table">
                    <thead>
                      <tr>
                        <th>Close Date</th>
                        <th>Ticket</th>
                        <th>Symbol</th>
                        <th>Side</th>
                        <th>Result</th>
                        <th>Volume</th>
                        <th>Open Price</th>
                        <th>Close Price</th>
                        <th>Open Time</th>
                        <th>Close Time</th>
                        <th>Commission</th>
                        <th>Swap</th>
                        <th>Profit</th>
                        <th>Net Profit</th>
                        <th>View</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!loading && !historyLoading && filteredTrades.length === 0 ? (
                        <tr>
                          <td colSpan={15}>
                            {selectedIsLive && hasDemoAccount
                              ? 'No closed trades on this Live account. Switch to DEMO to see practice prediction history.'
                              : 'No closed trades for this filter.'}
                          </td>
                        </tr>
                      ) : !loading && !historyLoading ? (
                        filteredTrades.map((t) => (
                          <tr key={`${t.ticket}-${t.close_time}`}>
                            <td>{formatDateOnly(t.close_time)}</td>
                            <td className="cp-th__num">{t.ticket ?? '—'}</td>
                            <td>{t.symbol || '—'}</td>
                            <td>{t.side || '—'}</td>
                            <td>
                              {(() => {
                                const raw = String(t.result || '').toUpperCase()
                                if (raw === 'WON') return 'Won'
                                if (raw === 'LOST') return 'Lost'
                                if (raw === 'BREAKEVEN') return 'Break-even'
                                const net = Number(t.net_profit ?? t.profit)
                                if (!Number.isFinite(net)) return '—'
                                if (net > 0) return 'Won'
                                if (net < 0) return 'Lost'
                                return 'Break-even'
                              })()}
                            </td>
                            <td className="cp-th__num">{t.volume ?? '—'}</td>
                            <td className="cp-th__num">{t.open_price ?? '—'}</td>
                            <td className="cp-th__num">{t.close_price ?? '—'}</td>
                            <td>{formatWhen(t.open_time)}</td>
                            <td>{formatWhen(t.close_time)}</td>
                            <td className="cp-th__num">
                              {moneySigned(t.commission, currency)}
                            </td>
                            <td className="cp-th__num">{moneySigned(t.swap, currency)}</td>
                            <td className={`cp-th__num ${plClass(t.profit)}`}>
                              {moneySigned(t.profit, currency)}
                            </td>
                            <td className={`cp-th__num ${plClass(t.net_profit ?? t.profit)}`}>
                              {moneySigned(t.net_profit ?? t.profit, currency)}
                            </td>
                            <td>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setDetail(t)}
                              >
                                View
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : null}
                    </tbody>
                  </table>
                </div>

                <div
                  className={`cp-th__cards${loading || historyLoading ? ' is-refreshing' : ''}`}
                  aria-label="Trade cards"
                >
                  {!loading && !historyLoading && filteredTrades.length === 0 ? (
                    <p className="cp-muted">
                      {selectedIsLive && hasDemoAccount
                        ? 'No closed trades on this Live account. Switch to DEMO to see practice prediction history.'
                        : 'No closed trades for this filter.'}
                    </p>
                  ) : !loading && !historyLoading ? (
                    filteredTrades.map((t) => (
                      <article key={`card-${t.ticket}-${t.close_time}`} className="cp-th__card">
                        <header>
                          <strong>{t.symbol || '—'}</strong>
                          <span>{t.side || '—'}</span>
                          <span className={`cp-th__num ${plClass(t.net_profit ?? t.profit)}`}>
                            {moneySigned(t.net_profit ?? t.profit, currency)}
                          </span>
                        </header>
                        <dl>
                          <div>
                            <dt>Volume</dt>
                            <dd className="cp-th__num">{t.volume ?? '—'}</dd>
                          </div>
                          <div>
                            <dt>Open</dt>
                            <dd className="cp-th__num">{t.open_price ?? '—'}</dd>
                          </div>
                          <div>
                            <dt>Close</dt>
                            <dd className="cp-th__num">{t.close_price ?? '—'}</dd>
                          </div>
                          <div>
                            <dt>Opened</dt>
                            <dd>{formatDateOnly(t.open_time)}</dd>
                          </div>
                          <div>
                            <dt>Closed</dt>
                            <dd>{formatDateOnly(t.close_time)}</dd>
                          </div>
                        </dl>
                        <Button type="button" variant="secondary" size="sm" onClick={() => setDetail(t)}>
                          View details
                        </Button>
                      </article>
                    ))
                  ) : null}
                </div>
              </>
            )
          ) : (
            <section
              className={`cp-th__activity${loading || historyLoading ? ' is-refreshing' : ''}`}
              aria-label="Account activity"
            >
              <p className="cp-muted">
                Balance operations and non-trade account movements. Demo opening balance is not a trade
                or a real deposit.
              </p>
              {historyLoading && !loading ? (
                <p className="cp-muted ap-inline-status" aria-live="polite">
                  Loading…
                </p>
              ) : null}
              {!loading && !historyLoading && historyUnavailable ? (
                <p className="cp-muted">Account activity is temporarily unavailable.</p>
              ) : !loading && !historyLoading && activities.length === 0 ? (
                <p className="cp-muted">No account activity for this filter.</p>
              ) : !loading && !historyLoading ? (
                <ul className="cp-th__activity-list">
                  {activities.map((a) => (
                    <li key={`${a.deal_or_ticket_id}-${a.timestamp}`}>
                      <div>
                        <strong>{activityTypeLabel(a.activity_type)}</strong>
                        <span>{formatWhen(a.timestamp)}</span>
                      </div>
                      <span className={`cp-th__num ${plClass(a.amount)}`}>
                        {moneySigned(a.amount, a.currency || currency)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          )}

          {totalPages > 1 ? (
            <div className="cp-th__pager">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={page <= 1 || historyLoading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <span className="cp-muted">
                Page {page} of {totalPages} · {total} total
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={page >= totalPages || historyLoading}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          ) : null}
        </>
      ) : null}

      {detail ? (
        <div className="cp-th__drawer-backdrop" role="presentation" onClick={() => setDetail(null)}>
          <aside
            className="cp-th__drawer"
            role="dialog"
            aria-label="Trade detail"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="cp-th__drawer-head">
              <div>
                <p className="cp-eyebrow">Trade detail</p>
                <h2>
                  {detail.symbol || 'Trade'} · {detail.side || '—'}
                </h2>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => setDetail(null)}>
                Close
              </Button>
            </header>
            <span className="cp-chip cp-chip--ok">MT5 Verified</span>
            <dl className="cp-th__detail-grid">
              <div>
                <dt>Ticket</dt>
                <dd className="cp-th__num">{detail.ticket ?? '—'}</dd>
              </div>
              <div>
                <dt>Position ID</dt>
                <dd className="cp-th__num">{detail.position_id ?? '—'}</dd>
              </div>
              <div>
                <dt>Symbol</dt>
                <dd>{detail.symbol || '—'}</dd>
              </div>
              <div>
                <dt>Side</dt>
                <dd>{detail.side || '—'}</dd>
              </div>
              <div>
                <dt>Volume</dt>
                <dd className="cp-th__num">{detail.volume ?? '—'}</dd>
              </div>
              <div>
                <dt>Open price</dt>
                <dd className="cp-th__num">{detail.open_price ?? '—'}</dd>
              </div>
              <div>
                <dt>Close price</dt>
                <dd className="cp-th__num">{detail.close_price ?? '—'}</dd>
              </div>
              <div>
                <dt>Open time</dt>
                <dd>{formatWhen(detail.open_time)}</dd>
              </div>
              <div>
                <dt>Close time</dt>
                <dd>{formatWhen(detail.close_time)}</dd>
              </div>
              <div>
                <dt>Commission</dt>
                <dd className="cp-th__num">{moneySigned(detail.commission, currency)}</dd>
              </div>
              <div>
                <dt>Swap</dt>
                <dd className="cp-th__num">{moneySigned(detail.swap, currency)}</dd>
              </div>
              <div>
                <dt>Fee</dt>
                <dd className="cp-th__num">{moneySigned(detail.fee, currency)}</dd>
              </div>
              <div>
                <dt>Profit</dt>
                <dd className={`cp-th__num ${plClass(detail.profit)}`}>
                  {moneySigned(detail.profit, currency)}
                </dd>
              </div>
              <div>
                <dt>Net profit</dt>
                <dd className={`cp-th__num ${plClass(detail.net_profit)}`}>
                  {moneySigned(detail.net_profit, currency)}
                </dd>
              </div>
            </dl>
          </aside>
        </div>
      ) : null}
    </section>
  )
}
